import { useCallback, useContext, useMemo, useRef } from 'react';
import { useHoverDirty } from 'react-use';
import { BackSection } from 'src/components/common/BackSection';
import { EventHeaderNavTabs } from 'src/components/Events/EventHeader/EventHeaderNavTabs';
import { EventHeaderWithActions } from 'src/components/Events/EventHeader/EventHeaderWithActions';
import { TabItem } from 'src/components/Events/EventPage/EventPageTabs';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Stack, Tooltip } from 'src/core/ui';
import { useGetEventFullInfo } from 'src/hooks/useGetEventFullInfo';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { PlusIcon } from 'src/svgs';
import {
  EventIdQueryParam,
  PurchaseDeeplinkQueryParam,
} from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { addDeepLinkUrlPart } from 'src/utils/deepLinkUtils';
import { getPurchaseOrderDetailsModalConfigWithDeepLink } from 'src/utils/purchaseUtils';
import { Event } from 'src/WebApiController';

import { PurchaseEventTabs } from '../PurchaseEventPage';
import * as styles from './PurchaseEventPageHeaderV2.css';
import { PurchaseEventPageHeaderWithNav } from './PurchaseEventPageHeaderWithNav';

export const PurchaseEventPageHeaderV2 = ({
  event,
  tabs,
  activeTab,
  setActiveTab,
  returnUrl,
  defaultTab,
  onUpdateDefaultTab,
}: {
  event: Event;
  returnUrl?: string;
  activeTab: PurchaseEventTabs;
  setActiveTab: (tab: PurchaseEventTabs) => void;
  tabs: TabItem<PurchaseEventTabs>[];
  defaultTab?: PurchaseEventTabs;
  onUpdateDefaultTab: (tab: PurchaseEventTabs) => void;
}) => {
  const { venue, performer } = useGetEventFullInfo(event);
  const isMobile = useMatchMedia('mobile');

  const selectedTab = useMemo(() => {
    const sanitzedTab =
      activeTab === PurchaseEventTabs.None
        ? PurchaseEventTabs.Purchases
        : activeTab;
    return tabs.find((tab) => tab.value === sanitzedTab);
  }, [activeTab, tabs]);

  return (
    <>
      <Stack
        gap={isMobile ? 's' : 'm'}
        direction="column"
        style={{ padding: `0 ${vars.spacing.sm}` }}
      >
        {isMobile && (
          <Stack alignItems="center" width="full">
            <BackSection
              returnUrl={returnUrl ?? '/purchases'}
              returnState={{
                fromViagVirtId: event.virtId,
              }}
              iconOnly
            />
            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
              <EventHeaderNavTabs<PurchaseEventTabs>
                defaultTab={defaultTab}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                onUpdateDefaultTab={onUpdateDefaultTab}
                tabs={tabs.map((tab) => ({
                  value: tab.value,
                  contentId: tab.contentId!,
                }))}
              />
            </div>
          </Stack>
        )}
        <EventHeaderWithActions
          event={event}
          performer={performer}
          venue={venue}
          actions={<EventContentAction event={event} />}
        />
        <PurchaseEventPageHeaderWithNav
          event={event}
          venue={venue}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabs={tabs}
        />
      </Stack>
      <div
        className={selectedTab?.className}
        style={{ width: '100%', paddingTop: vars.spacing.xs }}
      >
        {selectedTab?.content}
      </div>
    </>
  );
};

const EventContentAction = ({ event }: { event: Event }) => {
  const { setModal } = useContext(ModalContext);
  const elementRef = useRef<HTMLDivElement>(null);
  const isHovering = useHoverDirty(elementRef);
  const { isDarkMode } = useSiteTheme();

  const addPurchase = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.preventDefault();

      // To display addPurchaseModal for a concrete event,
      // the URL must contain ?eventId=<eventId>&uniquePurchaseId=0
      addDeepLinkUrlPart(EventIdQueryParam, event.virtId);
      addDeepLinkUrlPart(PurchaseDeeplinkQueryParam, '0');
      setModal(getPurchaseOrderDetailsModalConfigWithDeepLink());
    },
    [event.virtId, setModal]
  );

  return (
    <Tooltip
      open={isHovering}
      delayDuration={100}
      contentVariant={isDarkMode ? 'light' : 'dark'}
      align="center"
      triggerContent={
        <Stack
          ref={elementRef}
          onClick={addPurchase}
          className={styles.actionButton}
          gap="s"
          alignItems="center"
          style={{ color: isHovering ? vars.color.textBrand : undefined }}
        >
          <PlusIcon
            fill={isHovering ? vars.color.iconHover : vars.color.textPrimary}
            size={vars.iconSize.l}
          />
        </Stack>
      }
    >
      <Content id={ContentId.AddPurchase} />
    </Tooltip>
  );
};
